import { brl } from "../lib/periodos";

// Paleta categórica de referência (validada para daltonismo), em ordem fixa;
// cor por código porque o tema do painel troca as classes laranja pela cor da
// loja. "Sobrou"/"Faltou" usam as cores de status, nunca as das fatias.
const FATIAS = [
  { chave: "descontos", rotulo: "Descontos", claro: "#2a78d6", escuro: "#3987e5" },
  { chave: "taxas", rotulo: "Taxas", claro: "#eb6834", escuro: "#d95926" },
  { chave: "cmv", rotulo: "Compras (CMV)", claro: "#1baf7a", escuro: "#199e70" },
  { chave: "operacionais", rotulo: "Despesas", claro: "#eda100", escuro: "#c98500" },
  { chave: "naoOperacional", rotulo: "Outras", claro: "#e87ba4", escuro: "#d55181" },
];
const SOBROU = "#0ca30c";
const FALTOU = "#d03b3b";
const temaEscuro = () => typeof document !== "undefined" && document.documentElement.classList.contains("dark");

// Diferenças arredondadas ao centavo: sem isso, sobra de conta com vírgula
// (ex.: 0,00000000001) virava a fatia "Outras R$ 0,00" na legenda (25/10/2026).
const centavos = (v) => Math.round(v * 100) / 100;

function fatias(r) {
  const bruta = Number(r.receita_bruta) || 0;
  const taxas = Number(r.taxas) || 0;
  const liquida = Number(r.receita_liquida) || 0;
  const valores = {
    descontos: Math.max(centavos(bruta - liquida - taxas), 0),
    taxas,
    cmv: Number(r.cmv) || 0,
    operacionais: Number(r.despesas_operacionais) || 0,
    naoOperacional: Math.max(centavos((Number(r.lucro_bruto) || 0) - (Number(r.despesas_operacionais) || 0) - (Number(r.resultado_liquido) || 0)), 0),
  };
  return { bruta, valores, sobra: Number(r.resultado_liquido) || 0 };
}

/**
 * "De cada R$ 100 vendidos, quanto foi para onde" — a leitura que o dono entende
 * sem saber DRE. Com prejuízo, a barra mostra os custos e o quanto passaram da venda.
 */
export default function CadaCem({ resumo }) {
  const { bruta, valores, sobra } = fatias(resumo || {});
  if (bruta <= 0) return null;
  const custos = Object.values(valores).reduce((a, b) => a + b, 0);
  const base = sobra >= 0 ? bruta : custos;
  const por100 = (v) => (v / bruta) * 100;
  const cor = (f) => (temaEscuro() ? f.escuro : f.claro);
  const visiveis = FATIAS.filter((f) => valores[f.chave] > 0);
  return (
    <div className="rounded-2xl border bg-card p-4 sm:p-5" data-e2e="dre-cada-cem">
      <p className="text-sm font-semibold">De cada R$ 100 vendidos…</p>
      <div className="mt-3 h-4 rounded-full overflow-hidden flex gap-[2px] bg-muted">
        {visiveis.map((f) => (
          <div key={f.chave} style={{ width: `${(valores[f.chave] / base) * 100}%`, background: cor(f) }} title={`${f.rotulo}: ${brl(valores[f.chave])}`} />
        ))}
        {sobra > 0 && <div style={{ width: `${(sobra / base) * 100}%`, background: SOBROU }} title={`Sobrou: ${brl(sobra)}`} />}
      </div>
      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-xs">
        {visiveis.map((f) => (
          <span key={f.chave} className="flex items-center gap-1.5 whitespace-nowrap">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ background: cor(f) }} />
            <span className="text-muted-foreground">{f.rotulo}</span>
            <span className="font-semibold tabular-nums">{brl(por100(valores[f.chave]))}</span>
          </span>
        ))}
        <span className="flex items-center gap-1.5 whitespace-nowrap font-semibold">
          <span className="w-2.5 h-2.5 rounded-sm" style={{ background: sobra >= 0 ? SOBROU : FALTOU }} />
          <span>{sobra >= 0 ? "Sobrou" : "Faltou"}</span>
          <span className="tabular-nums">{brl(Math.abs(por100(sobra)))}</span>
        </span>
      </div>
      {sobra < 0 && <p className="text-xs text-muted-foreground mt-2">Os gastos passaram das vendas: a barra mostra como eles se dividem.</p>}
    </div>
  );
}
